import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PermissionsProvider, usePermissions } from "./context/PermissionsContext";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

import Tickets from "./pages/Tickets";
import NewTicket from "./pages/NewTicket";
import TicketDetails from "./pages/TicketDetails";

import Assets from "./pages/Assets";
import NewAsset from "./pages/NewAsset";
import AssetDetails from "./pages/AssetDetails";

import Stock from "./pages/Stock";
import NewStockItem from "./pages/NewStockItem";
import StockCatalogs from "./pages/StockCatalogs";
import StockPurchaseRequests from "./pages/StockPurchaseRequests";
import StockRequests from "./pages/StockRequests";

import Users from "./pages/Users";
import NewUser from "./pages/NewUser";

import Collaborators from "./pages/Collaborators";
import BaseConhecimento from "./pages/BaseConhecimento";
import ArtigoConhecimento from "./pages/ArtigoConhecimento";
import KBPublica from "./pages/KBPublica";
import KBPublicaArtigo from "./pages/KBPublicaArtigo";
import Assinaturas from "./pages/Assinaturas";
import CollaboratorDepartments from "./pages/CollaboratorDepartments";
import FluxogramaColaboradores from "./pages/FluxogramaColaboradores";
import NewCollaborator from "./pages/NewCollaborator";
import CollaboratorDetails from "./pages/CollaboratorDetails";

import Settings from "./pages/Settings";
import Permissoes from "./pages/Permissoes";
import Auditoria from "./pages/Auditoria";
import SaudePlataforma from "./pages/SaudePlataforma";
import CategoriasChamado from "./pages/CategoriasChamado";
import Relatorios from "./pages/Relatorios";
import Sla from "./pages/Sla";
import Calendario from "./pages/Calendario";
import Tenants from "./pages/Tenants";
import Perfil from "./pages/Perfil";
import MinhaAtividade from "./pages/MinhaAtividade";
import Instalar from "./pages/Instalar";

import "./App.css";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const isAdmin =
    user?.profile?.toLowerCase() === "administrador" ||
    user?.profile?.toLowerCase() === "admin";

  return isAdmin ? children : <Navigate to="/dashboard" />;
};

const PlatformAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return user?.is_platform_admin ? children : <Navigate to="/dashboard" />;
};

// Bloqueia o acesso a uma tela quando o perfil nao tem "Ver" nela (Fase 4).
// Admin/owner fazem bypass; rota negada redireciona pra primeira visivel.
const ScreenRouteGuard = ({ route, children }) => {
  const { loading } = useAuth();
  const { ready, can, firstVisible } = usePermissions();

  if (loading || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return can("view", route) ? children : <Navigate to={firstVisible} replace />;
};

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <BrowserRouter>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/kb" element={<KBPublica />} />
          <Route path="/kb/:id" element={<KBPublicaArtigo />} />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/tickets"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/tickets">
                  <Tickets />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/tickets/new"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/tickets">
                  <NewTicket />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/tickets/:id"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/tickets">
                  <TicketDetails />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/assets"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/assets">
                  <Assets />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/assets/new"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/assets">
                  <NewAsset />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/assets/:id"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/assets">
                  <AssetDetails />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/collaborators"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/collaborators">
                  <Collaborators />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/collaborators/departments"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/collaborators">
                  <CollaboratorDepartments />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/collaborators/fluxograma"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/collaborators">
                  <FluxogramaColaboradores />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/collaborators/new"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/collaborators">
                  <NewCollaborator />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/collaborators/:id"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/collaborators">
                  <CollaboratorDetails />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/stock"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/stock">
                  <Stock />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/stock/new"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/stock">
                  <NewStockItem />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/stock/catalogs"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/stock">
                  <StockCatalogs />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/stock/purchase-requests"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/stock">
                  <StockPurchaseRequests />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/stock/requests"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/stock">
                  <StockRequests />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/base-conhecimento"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/base-conhecimento">
                  <BaseConhecimento />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/base-conhecimento/:id"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/base-conhecimento">
                  <ArtigoConhecimento />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/assinaturas"
            element={
              <PrivateRoute>
                <ScreenRouteGuard route="/assinaturas">
                  <Assinaturas />
                </ScreenRouteGuard>
              </PrivateRoute>
            }
          />

          <Route
            path="/users"
            element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            }
          />

          <Route
            path="/users/new"
            element={
              <AdminRoute>
                <NewUser />
              </AdminRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <AdminRoute>
                <Settings />
              </AdminRoute>
            }
          />

          <Route
            path="/permissoes"
            element={
              <AdminRoute>
                <Permissoes />
              </AdminRoute>
            }
          />

          <Route
            path="/auditoria"
            element={
              <AdminRoute>
                <Auditoria />
              </AdminRoute>
            }
          />

          <Route
            path="/saude"
            element={
              <AdminRoute>
                <SaudePlataforma />
              </AdminRoute>
            }
          />

          <Route
            path="/categorias"
            element={
              <AdminRoute>
                <CategoriasChamado />
              </AdminRoute>
            }
          />

          <Route
            path="/relatorios"
            element={
              <AdminRoute>
                <Relatorios />
              </AdminRoute>
            }
          />

          <Route
            path="/sla"
            element={
              <AdminRoute>
                <Sla />
              </AdminRoute>
            }
          />

          <Route
            path="/calendario"
            element={
              <AdminRoute>
                <Calendario />
              </AdminRoute>
            }
          />

          <Route
            path="/tenants"
            element={
              <PlatformAdminRoute>
                <Tenants />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="/perfil"
            element={
              <PrivateRoute>
                <Perfil />
              </PrivateRoute>
            }
          />

          <Route
            path="/minha-atividade"
            element={
              <PrivateRoute>
                <MinhaAtividade />
              </PrivateRoute>
            }
          />

          <Route
            path="/instalar"
            element={
              <PrivateRoute>
                <Instalar />
              </PrivateRoute>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
